/**
 * PublicAnnouncementService
 * Service for public announcement endpoints (read-only)
 * Route: api/public/announcements
 */
import { BaseService } from '../base/BaseService';
/**
 * Public read-only service for announcements
 * All data is publicly accessible without authentication
 */
export class PublicAnnouncementService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicAnnouncementService.DEFAULT_SERVICE_PATH,
            basePath: PublicAnnouncementService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get all active announcements
     * GET /api/public/announcements
     * @returns Array of active public announcement views
     */
    async getActive() {
        const response = await this.getActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get all active announcements (raw response)
     */
    getActiveRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    /**
     * Get a single announcement by ID
     * GET /api/public/announcements/{id}
     * @param id - Announcement ID
     * @returns Public announcement model view
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Announcement ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get announcement by ID (raw response)
     * @param id - Announcement ID
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Get announcements by location
     * GET /api/public/announcements/by-location/{locationId}
     * @param locationId - Location ID
     * @returns Array of public announcement views for the location
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get announcements by location (raw response)
     * @param locationId - Location ID
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('by-location', `by-location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get announcements by event
     * GET /api/public/announcements/by-event/{eventId}
     * @param eventId - Event ID
     * @returns Array of public announcement views for the event
     */
    async getByEventId(eventId) {
        const response = await this.getByEventIdRaw(eventId);
        return response.data ?? [];
    }
    /**
     * Get announcements by event (raw response)
     * @param eventId - Event ID
     */
    getByEventIdRaw(eventId) {
        const endpoint = this.getEndpoint('by-event', `by-event/${eventId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get featured announcements
     * GET /api/public/announcements/featured
     * @returns Array of featured public announcement views
     */
    async getFeatured() {
        const response = await this.getFeaturedRaw();
        return response.data ?? [];
    }
    /**
     * Get featured announcements (raw response)
     */
    getFeaturedRaw() {
        const endpoint = this.getEndpoint('featured', 'featured');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get upcoming announcements by location
     * GET /api/public/announcements/upcoming/by-location/{locationId}
     * @param locationId - Location ID
     * @returns Array of upcoming public announcement views for the location
     */
    async getUpcomingByLocationId(locationId) {
        const response = await this.getUpcomingByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get upcoming announcements by location (raw response)
     * @param locationId - Location ID
     */
    getUpcomingByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('upcoming-by-location', `upcoming/by-location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
PublicAnnouncementService.DEFAULT_SERVICE_PATH = 'announcements';
PublicAnnouncementService.BASE_PATH = 'api/public/announcements';
//# sourceMappingURL=PublicAnnouncementService.js.map